// SimList – paginated SIM table with sorting, filters, and drill-down details.

import React from 'react';
import { Sim } from '../../../types/Sim';
import RefreshButton from '../shared/RefreshButton';
import TableLoadingOverlay from '../shared/TableLoadingOverlay';
import CyclingLoader from '../shared/CyclingLoader';

// ---- Types ----

type SortConfig = {
  key: keyof Sim | 'last_came_online_at' | 'totalMB';
  direction: 'asc' | 'desc';
};

type Props = {
  sims: Sim[];
  loading: boolean;
  error: string | null;
  activatedOnly: boolean;
  sortConfig: SortConfig | null;
  currentPage: number;
  expandedSim: string | null;
  openSection: 'sim' | 'data' | 'device' | null;
  onToggleActivatedOnly: (value: boolean) => void;
  onSort: (key: SortConfig['key']) => void;
  onToggleExpand: (id: string) => void;
  onPageChange: (page: number) => void;
  onRefresh: () => void;
  onSectionToggle: (section: 'sim' | 'data' | 'device' | null) => void;
};

// ---- Constants ----

const PAGE_SIZE = 22;

// ---- Small helpers ----

function getSortIndicator(sortConfig: SortConfig | null, key: SortConfig['key']) {
  if (!sortConfig || sortConfig.key !== key) return null;
  return sortConfig.direction === 'asc' ? '▲' : '▼';
}

function renderDropdownSection(
  label: string,
  content: React.ReactNode,
  section: Props['openSection'],
  openSection: Props['openSection'],
  onSectionToggle: Props['onSectionToggle'],
) {
  // One little collapsible block under each SIM row (SIM / data / device).
  return (
    <div className="border-t pt-2">
      <button
        onClick={() => onSectionToggle(openSection === section ? null : section)}
        className="w-full text-left font-semibold text-sm text-gray-700 dark:text-slate-300 hover:text-blue-600"
      >
        {label} {openSection === section ? '▲' : '▼'}
      </button>
      {openSection === section && (
        <div className="pl-4 mt-2 space-y-1 text-sm text-gray-800 dark:text-slate-200">{content}</div>
      )}
    </div>
  );
}

// ---- Component ----

export default function SimList(props: Props) {
  const {
    sims,
    loading,
    error,
    activatedOnly,
    sortConfig,
    currentPage,
    expandedSim,
    openSection,
    onToggleActivatedOnly,
    onSort,
    onToggleExpand,
    onPageChange,
    onRefresh,
    onSectionToggle,
  } = props;

  // First trim the list down to just the SIMs we care about.
  const filteredSims = activatedOnly ? sims.filter((sim) => sim.activated) : sims;

  // Then sort whatever is left, based on the current column + direction.
  const sortedSims = React.useMemo(() => {
    if (!sortConfig) return filteredSims;

    return [...filteredSims].sort((a, b) => {
      let aVal: any = a[sortConfig.key as keyof Sim];
      let bVal: any = b[sortConfig.key as keyof Sim];

      // Some “columns” aren’t raw fields, so we normalise them here.
      if (sortConfig.key === 'last_came_online_at') {
        aVal = a.last_came_online_at ? new Date(a.last_came_online_at).getTime() : 0;
        bVal = b.last_came_online_at ? new Date(b.last_came_online_at).getTime() : 0;
      } else if (sortConfig.key === 'totalMB') {
        aVal = a.totalMB ?? 0;
        bVal = b.totalMB ?? 0;
      }

      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredSims, sortConfig]);

  // Finally, slice the sorted result into pages so we don’t render a mile-long table.
  const paginatedSims = React.useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return sortedSims.slice(start, start + PAGE_SIZE);
  }, [sortedSims, currentPage]);

  // ---- Loading / empty / error shells ----

  if (loading && sims.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen">
        <CyclingLoader
          phrases={['Loading SIMs…', 'Fetching carrier data…']}
          size="md"
        />
      </div>
    );
  }

  if (!sims.length && !error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen space-y-4">
        <button
          onClick={onRefresh}
          className="px-6 py-2 bg-blue-500 text-white rounded shadow hover:bg-blue-600"
        >
          Fetch SIMs
        </button>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen space-y-4">
        <div className="text-red-600 text-lg">Error: {error}</div>
        <button
          onClick={onRefresh}
          className="px-6 py-2 bg-blue-500 text-white rounded shadow hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    );
  }

  // ---- Main render ----

  return (
    <div className="p-4">
      {/* Header row – title, filters and refresh */}
      <div className="flex space-x-4 items-center mb-4">
        <div className="flex items-center space-x-4">
          <h2 className="text-2xl font-semibold">SIM List</h2>
          <label className="inline-flex items-center">
            <input
              type="checkbox"
              checked={activatedOnly}
              onChange={(e) => onToggleActivatedOnly(e.target.checked)}
              className="mr-2 rounded border-gray-300 dark:border-slate-600 focus:ring-blue-500"
            />
            <span className="text-sm text-gray-700 dark:text-slate-300">Activated Only</span>
          </label>
        </div>
        <RefreshButton
          busy={loading}
          onClick={onRefresh}
          className="px-4 py-1 text-sm bg-white dark:bg-slate-900 border border-gray-300 dark:border-slate-600 rounded shadow-sm hover:bg-gray-50 hover:border-gray-400"
          busyText="Loading..."
          busyClassName="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-slate-400"
        />
      </div>

      {/* Table of SIMs */}
      <TableLoadingOverlay loading={loading} label="Loading SIMs...">
      <div className="overflow-x-auto rounded-lg shadow">
        <table className="min-w-full table-auto text-sm text-left border border-gray-200 dark:border-slate-700">
          <thead className="bg-gray-100 dark:bg-slate-800 text-gray-700 cursor-pointer select-none">
            <tr>
              <th onClick={() => onSort('label')} className="px-4 py-2">
                Label {getSortIndicator(sortConfig, 'label')}
              </th>
              <th onClick={() => onSort('iccid')} className="px-4 py-2">
                ICCID {getSortIndicator(sortConfig, 'iccid')}
              </th>
              <th onClick={() => onSort('activated')} className="px-4 py-2">
                Activated {getSortIndicator(sortConfig, 'activated')}
              </th>
              <th onClick={() => onSort('online')} className="px-4 py-2">
                Online {getSortIndicator(sortConfig, 'online')}
              </th>
              <th onClick={() => onSort('last_came_online_at')} className="px-4 py-2">
                Last Online {getSortIndicator(sortConfig, 'last_came_online_at')}
              </th>
              <th onClick={() => onSort('totalMB')} className="px-4 py-2">
                Usage (MB) {getSortIndicator(sortConfig, 'totalMB')}
              </th>
              <th className="px-4 py-2">Network</th>
              <th onClick={() => onSort('connector')} className="px-4 py-2">
                Connector {getSortIndicator(sortConfig, 'connector')}
              </th>
              <th className="px-4 py-2">Tags</th>
              <th className="px-4 py-2">Details</th>
            </tr>
          </thead>

          <tbody className="text-gray-800 dark:text-slate-200">
            {paginatedSims.map((sim) => (
              <React.Fragment key={sim.id}>
                <tr className="border-t">
                  <td className="px-4 py-2">{sim.label ?? '(No label)'}</td>
                  <td className="px-4 py-2">{sim.iccid}</td>
                  <td className="px-4 py-2">{sim.activated ? 'Yes' : 'No'}</td>
                  <td className="px-4 py-2">{sim.online ? 'Yes' : 'No'}</td>
                  <td className="px-4 py-2">
                    {sim.last_came_online_at
                      ? new Date(sim.last_came_online_at).toLocaleString()
                      : 'Never'}
                  </td>
                  <td className="px-4 py-2">{sim.totalMB?.toFixed(2) ?? '0.00'}</td>
                  <td className="px-4 py-2">
                    {sim.network?.name ?? 'Unknown'} ({sim.network?.country_code ?? 'N/A'})
                  </td>
                  <td className="px-4 py-2">{sim.connector ?? 'None'}</td>
                  <td className="px-4 py-2">
                    {sim.tags.map((t) => t.name).join(', ') || 'None'}
                  </td>
                  <td className="px-4 py-2">
                    <button
                      onClick={() => onToggleExpand(sim.id)}
                      className="text-blue-600 hover:underline"
                    >
                      {expandedSim === sim.id ? 'Hide' : 'Show'}
                    </button>
                  </td>
                </tr>

                {expandedSim === sim.id && (
                  <tr>
                    <td colSpan={10} className="bg-gray-50 dark:bg-slate-800 px-4 py-4">
                      {renderDropdownSection(
                        'SIM Information',
                        <>
                          <p>
                            <strong>IPV4:</strong> {sim.ipv4 ?? 'n/a'}
                          </p>
                          <p>
                            <strong>IMSI:</strong> {sim.imsi ?? 'n/a'}
                          </p>
                          <p>
                            <strong>MSISDN:</strong> {sim.msisdn ?? 'n/a'}
                          </p>
                          <p>
                            <strong>Country:</strong> {sim.country ?? 'n/a'}
                          </p>
                          <p>
                            <strong>Network:</strong> {sim.network?.name ?? 'n/a'}
                          </p>
                          <p>
                            <strong>MCC:</strong> {sim.mcc ?? 'n/a'}
                          </p>
                          <p>
                            <strong>MNC:</strong> {sim.mnc ?? 'n/a'}
                          </p>
                        </>,
                        'sim',
                        openSection,
                        onSectionToggle,
                      )}

                      {renderDropdownSection(
                        'Data Limits',
                        <>
                          <p>
                            <strong>Monthly Limit:</strong> {sim.monthly_limit ?? 'n/a'} MB
                          </p>
                          <p>
                            <strong>Period Usage:</strong>{' '}
                            {sim.usageTotalMB?.toFixed(2) ?? '0.00'} MB
                          </p>
                          <p>
                            <strong>Lifetime Usage:</strong> {sim.lifetime_usage ?? 'n/a'} MB
                          </p>
                          <p>
                            <strong>Resets At:</strong>{' '}
                            {sim.resets_at
                              ? new Date(sim.resets_at).toUTCString()
                              : 'n/a'}
                          </p>
                        </>,
                        'data',
                        openSection,
                        onSectionToggle,
                      )}

                      {renderDropdownSection(
                        'Device Info',
                        <>
                          <p>
                            <strong>IMEI:</strong> {sim.imei ?? 'n/a'}
                          </p>
                          <p>
                            <strong>IMEI Lock:</strong> {sim.imei_lock ?? 'n/a'}
                          </p>
                          <p>
                            <strong>Modem Model:</strong> {sim.modem_model ?? 'n/a'}
                          </p>
                          <p>
                            <strong>Modem Manufacturer:</strong>{' '}
                            {sim.modem_manufacturer ?? 'n/a'}
                          </p>
                          <p>
                            <strong>Technologies:</strong>{' '}
                            {sim.enabled_technologies ?? 'n/a'}
                          </p>
                        </>,
                        'device',
                        openSection,
                        onSectionToggle,
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      </TableLoadingOverlay>

      {/* Pagination footer */}
      <div className="flex justify-center mt-4 space-x-2">
        {Array.from({ length: Math.ceil(sortedSims.length / PAGE_SIZE) }, (_, i) => (
          <button
            key={i}
            onClick={() => onPageChange(i + 1)}
            className={`px-3 py-1 border rounded ${
              currentPage === i + 1
                ? 'bg-blue-500 text-white'
                : 'bg-white dark:bg-slate-900 text-gray-700 hover:bg-gray-100'
            }`}
          >
            {i + 1}
          </button>
        ))}
      </div>
    </div>
  );
}